import React from 'react';
import { FloorPlan } from '../types';
import './FloorPlanViewer.css'; // We'll share the CSS file
import { MinimapViewportIndicator } from './MinimapViewportIndicator';
interface MinimapProps {
  planData: FloorPlan;
  viewState: { scale: number; translateX: number; translateY: number };
  viewerDimensions: { width: number; height: number };
  onJump: (newCenter: { x: number; y: number }) => void;
}

export const Minimap = ({ planData, viewState, viewerDimensions, onJump }: MinimapProps) => {
  const minimapRef = React.useRef<HTMLDivElement>(null);
  
  if (!viewerDimensions.width || !viewerDimensions.height) {
    return null; // Don't render until we have the main viewer's dimensions
  }

  // Calculate the scale to fit the entire floor plan into the minimap width (e.g., 200px)
  const minimapWidth = 200;
  const minimapScale = minimapWidth / planData.dimensions.width;
  const minimapHeight = planData.dimensions.height * minimapScale;
 
  const handleMinimapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!minimapRef.current) return;

    // Get click coordinates relative to the minimap content area
    const rect = minimapRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert minimap click coordinates to main floor plan coordinates
    const targetX = clickX / minimapScale;
    const targetY = clickY / minimapScale;

    // Tell the parent component to jump to this new center
    onJump({ x: targetX, y: targetY });
  };

  return (
    <div className="minimap-container">
      <div
        ref={minimapRef}
        className="minimap-content"
        style={{ width: minimapWidth, height: minimapHeight }}
        onClick={handleMinimapClick}
      >
        {/* Render simplified rooms for the overview */}
        {planData.rooms.map(room => (
          <div
            key={`mini-${room.id}`}
            className="minimap-room"
            style={{
              left: `${(room.position.x - planData.dimensions.minX) * minimapScale}px`,
              top: `${(room.position.y - planData.dimensions.minY) * minimapScale}px`,
              width: `${room.size.width * minimapScale}px`,
              height: `${room.size.height * minimapScale}px`,
            }}
          />
        ))}
        {planData.facilities.map(facility => (
          <div
            key={`mini-${facility.id}`}
            className="minimap-facility"
            style={{
              left: `${(facility.position.x - planData.dimensions.minX) * minimapScale}px`,
              top: `${(facility.position.y - planData.dimensions.minY) * minimapScale}px`,
              width: `${facility.size.width * minimapScale}px`,
              height: `${facility.size.height * minimapScale}px`,
            }}
          />
        ))}
        {/* Use the new SVG-based viewport indicator */}
        <MinimapViewportIndicator
          viewState={viewState}
          viewerDimensions={viewerDimensions}
          planData={planData}
          minimapScale={minimapScale}
          rotationX={10}   // Must match the CSS
          rotationZ={-10}  // Must match the CSS
        />
      </div>
    </div>
  );
};